import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as translator from "../../../../utils/translator";
import * as http from "../../../../utils/http";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import vCheck from "../../../../icons/vCheck.svg";

const SaleVoucherWidget: React.FC = () => {
    const { language, saleVoucherCode, setSaleVoucherCode, setIsVoucherValid } = useShippingPageContext();
    const [isLoading, setIsLoading] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [searchParams] = useSearchParams();

    // Auto-apply voucher from the `cpn` query param on page load
    useEffect(() => {
        const queryVoucherCode = searchParams.get('cpn')?.trim();
        if (queryVoucherCode) {
            setSaleVoucherCode(queryVoucherCode);
            applySaleVoucher(queryVoucherCode);
        }
    }, []);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSaleVoucherCode(e.target.value);
        // Reset states when user changes input
        setIsValid(false);
        setIsVoucherValid(false);
        setErrorMsg("");
    };

    const applySaleVoucher = (code: string) => {
        

        setIsLoading(true);
        setErrorMsg("");

        http.postToServer('shipping/check-sale-voucher', {
            saleVoucherCode: code.trim()
        }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID])
            .then((response) => {
                if (response.data.status === 'success') {
                    setIsValid(true);
                    setIsVoucherValid(true);
                }
            })
            .catch((error: any) => {
                const errorMessage = error?.response?.data?.status === "sale_voucher_already_used" ? translator.translate(language, "payment", "vouchers/sale_voucher_already_used_title") : translator.translate(language, "payment", "vouchers/invalid_sale_voucher_title");
                setErrorMsg(errorMessage);
                setIsValid(false);
                setIsVoucherValid(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!saleVoucherCode.trim()) return;

        applySaleVoucher(saleVoucherCode)

    }
    

    const handleReset = () => {
        setSaleVoucherCode("");
        setIsValid(false);
        setIsVoucherValid(false);
        setErrorMsg("");
    };

    // If voucher is valid, show confirmation view
    if (isValid) {
        return (
            <>
                {/* Title */}
                <span id='sale-voucher-widget-title-confirmed' className='font-semibold font-roboto text-[#98a0af] text-[13px] my-[15px] block'>
                    {translator.translate(language, "shipping", "SaleVoucherWidget/title") || "Sale Voucher"}
                </span>

                {/* Voucher Confirmed Container */}
                <div
                    id="confirmed-sale-voucher-widget-container"
                    style={{ border: "1px solid var(--primary-lightgreen)" }}
                    className="relative flex items-center justify-between bg-transparent h-14 w-full rounded-md px-5 transition-all duration-200 text-[13px] font-medium text-[var(--primary-lightgreen)] cursor-default"
                >
                    <p>{saleVoucherCode}</p>
                    <img className="sale-voucher-confirmed-vCheck" src={vCheck} alt="check" />
                </div>

                {/* Change Voucher button */}
                <p
                    onClick={handleReset}
                    className="text-[12px] text-[#269C6E] cursor-pointer mt-2 hover:underline"
                >
                    {translator.translate(language, "shipping", "SaleVoucherWidget/change") || "Change voucher"}
                </p>
            </>
        );
    }

    return (
        <>
            {/* Title */}
            <span id='sale-voucher-widget-title-input' className='font-semibold font-roboto text-[#98a0af] text-[13px] my-[15px] block'>
                {translator.translate(language, "shipping", "SaleVoucherWidget/title") || "Sale Voucher"}
            </span>

            {/* Enter Sale Voucher Container */}
            <form
                id="sale-voucher-widget-container"
                onSubmit={handleOnSubmit}
                style={errorMsg ? { border: "1px solid #ef4747" } : {}}
                className="relative flex items-center justify-between bg-white h-14 w-full rounded-md px-[20px] pr-[95px] transition-all duration-200"
            >
                {/* Voucher input field */}
                <input
                    id="sale-voucher-widget-input"
                    value={saleVoucherCode}
                    className="text-[13px] h-full w-full border-none outline-none overflow-hidden pr-6"
                    onChange={handleOnChange}
                    maxLength={25}
                    type="text"
                    placeholder={translator.translate(language, "shipping", "SaleVoucherWidget/placeholder") || "Enter voucher code"}
                />

                {/* Submit Button */}
                <button
                    id="sale-voucher-widget-submit-button"
                    style={{ border: "2px solid #269C6E" }}
                    className="absolute flex items-center justify-center w-fit min-w-[67px] text-[12px] font-bold text-[#269C6E] bg-transparent h-9 rounded-[3px] px-[10px] py-[10px] right-[10px] top-[10px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={saleVoucherCode === "" || isLoading}
                    type="submit"
                >
                    <p>{isLoading ? "..." : (translator.translate(language, "shipping", "SaleVoucherWidget/apply") || "Apply")}</p>
                </button>
            </form>

            {/* Error Message */}
            {errorMsg && (
                <span id="sale-voucher-error-message" className="text-[12px] w-fit my-1 font-medium text-[#ef4747]">
                    {errorMsg}
                </span>
            )}
        </>
    );
};

export default SaleVoucherWidget; 